import React, { useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Link, Stack, Step, StepLabel, Stepper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { UploadFile as UploadFileIcon, Download as DownloadIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Preview statuses (what would happen) and import statuses (what did happen).
const STATUS = {
  new: { label: 'New — invite', color: 'success' },
  linked: { label: 'Existing account — link', color: 'info' },
  duplicate: { label: 'Already here — skip', color: 'default' },
  invalid: { label: 'Needs fixing', color: 'error' },
  created: { label: 'Created', color: 'success' },
  skipped: { label: 'Skipped', color: 'default' },
  failed: { label: 'Not imported', color: 'error' },
};
const ORDER = ['invalid', 'failed', 'duplicate', 'skipped', 'new', 'created', 'linked'];

/** A cell that a spreadsheet would read as a formula is prefixed so it stays text. */
const csvCell = (v) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function downloadResults(rows) {
  const header = ['row', 'full_name', 'email', 'phone', 'date_of_birth', 'reason', 'status', 'problem'];
  const lines = [header.join(',')].concat(rows.map((r) => [
    r.row, r.full_name, r.email, r.phone, r.date_of_birth, r.reason, r.status, (r.errors || []).join('; '),
  ].map(csvCell).join(',')));
  const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'patient-import-results.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function Counts({ counts }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {ORDER.filter((k) => counts?.[k]).map((k) => (
        <Chip key={k} size="small" color={STATUS[k].color}
          variant={STATUS[k].color === 'default' ? 'outlined' : 'filled'}
          label={`${counts[k]} ${STATUS[k].label.toLowerCase()}`} />
      ))}
    </Stack>
  );
}

function RowsTable({ rows }) {
  // Problems first: they are what the person has to act on.
  const sorted = [...rows].sort((a, b) =>
    ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.row - b.row);
  return (
    <TableContainer sx={{ maxHeight: 380, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Row</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Date of birth</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.row}>
              <TableCell>{r.row}</TableCell>
              <TableCell>{r.full_name || '—'}</TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>{r.email || '—'}</TableCell>
              <TableCell>{r.date_of_birth || '—'}</TableCell>
              <TableCell>
                <Chip size="small" color={STATUS[r.status]?.color || 'default'}
                  variant={STATUS[r.status]?.color === 'default' ? 'outlined' : 'filled'}
                  label={STATUS[r.status]?.label || r.status} />
                {r.errors?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {r.errors.join(' · ')}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Bulk patient import: pick a CSV/Excel file, see what would happen to every row, then
 * import. The server keeps nothing between preview and import, so the same file is
 * sent twice and checked again at import time.
 *
 * `api` is 'admin' (Manage Patients — admins and staff) or 'clinical' (a provider's Add
 * Patient, where everyone imported is assigned to them). Providers always send the
 * invitations: holding them back relies on Re-send in Manage Patients, which they
 * cannot open.
 */
export default function PatientImportDialog({ open, onClose, onImported, api = 'admin' }) {
  const isProvider = api === 'clinical';
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);

  const step = result ? 2 : preview ? 1 : 0;
  const importable = (preview?.counts?.new || 0) + (preview?.counts?.linked || 0);

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setSendInvites(true);
    if (inputRef.current) inputRef.current.value = '';
  };
  const close = () => { if (busy) return; reset(); onClose(); };

  const send = (path, extra = {}) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(extra).forEach(([k, v]) => form.append(k, v));
    return axios.post(`${API_URL}/api/v1/${api}/patients/${path}`, form);
  };

  const errorText = (err, fallback) => {
    const d = err.response?.data?.detail;
    return typeof d === 'string' ? d : fallback;
  };

  const handlePreview = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const res = await send('import/preview');
      setPreview(res.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not read that file'));
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await send('import', { send_invites: sendInvites ? 'true' : 'false' });
      setResult(res.data);
      const done = (res.data.counts.created || 0) + (res.data.counts.linked || 0);
      toast.success(`${done} patient${done === 1 ? '' : 's'} imported`);
      onImported?.();
    } catch (err) {
      toast.error(errorText(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/${api}/patients/import/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'patient-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the template');
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Import patients</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {['Choose file', 'Check', 'Done'].map((l) => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        {step === 0 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV or Excel (.xlsx) file with one patient per row — up to 1,000 at a time.
              Required columns: <b>full_name</b> (or first_name and last_name) and <b>email</b>.
              Optional: phone, date_of_birth (YYYY-MM-DD or MM/DD/YYYY), reason.
              Other columns are ignored.
              {isProvider && ' Everyone you import is assigned to you and emailed an invitation to the patient portal.'}
            </Typography>
            <Link component="button" type="button" onClick={downloadTemplate}
              sx={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <DownloadIcon fontSize="small" /> Download the template
            </Link>
            <Box sx={{ border: 2, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <input ref={inputRef} type="file" accept=".csv,.xlsx" hidden
                data-testid="patient-import-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => inputRef.current?.click()}>
                {file ? 'Choose a different file' : 'Choose file'}
              </Button>
              {file && <Typography variant="body2" sx={{ mt: 1 }}>{file.name}</Typography>}
            </Box>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <Counts counts={preview.counts} />
            {preview.ignored_columns?.length > 0 && (
              <Alert severity="info">Ignored columns: {preview.ignored_columns.join(', ')}</Alert>
            )}
            {(preview.counts.invalid || 0) > 0 && (
              <Alert severity="warning">
                Rows marked “Needs fixing” will not be imported. You can import the rest now and
                upload a corrected file for those later — rows already imported are skipped.
              </Alert>
            )}
            <RowsTable rows={preview.rows} />
            {!isProvider && (
              <FormControlLabel
                control={<Checkbox checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} />}
                label="Email each patient their portal invitation now"
              />
            )}
            {!isProvider && !sendInvites && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                Patients are registered without being emailed. Send an invitation later with
                “Re-send portal invite” on their row.
              </Typography>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Counts counts={result.counts} />
            {result.invites_queued > 0 && (
              <Alert severity="success">
                {result.invites_queued} invitation email{result.invites_queued === 1 ? ' is' : 's are'} on the way.
              </Alert>
            )}
            <RowsTable rows={result.rows} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={close} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={handlePreview} disabled={!file || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
              {busy ? 'Checking…' : 'Check file'}
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => { setPreview(null); }} disabled={busy}>Back</Button>
            <Button variant="contained" onClick={handleImport} disabled={!importable || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
              {busy ? 'Importing…' : `Import ${importable} patient${importable === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button startIcon={<DownloadIcon />} onClick={() => downloadResults(result.rows)}>
              Download results
            </Button>
            <Button variant="contained" onClick={close}>Done</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
